import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { Distributor, Fund, StrategyKey, SuccessFeeRun } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { StatementButtons } from '../../components/StatementButtons'
import { apiError } from '../../api/errors'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { SectionCard } from '../../components/SectionCard'
import { SegmentedTabs } from '../../components/SegmentedTabs'
import { StatusChip } from '../../components/StatusChip'
import { parseAmount } from '../../validation'
import { formatCzk, formatUnits } from '../jednotky/format'
import { InfoHint } from '../../components/InfoHint'
import { FeeChecksPanel } from './FeeChecksPanel'
import { downloadSuccessFeeReport } from './report'

const currentYear = () => new Date().getFullYear()

/** Potvrzení success fee — dvojité potvrzení (nevratné stržení) + email checkbox (F6-D). */
function ConfirmSuccessFeeDialog({
  open,
  fundName,
  totalFeeUnits,
  clientCount,
  submitting,
  error = null,
  onConfirm,
  onClose,
}: {
  open: boolean
  fundName: string
  totalFeeUnits: string
  clientCount: number
  submitting: boolean
  /** Chyba potvrzení; musí být UVNITŘ dialogu, jinak ji překryje backdrop. */
  error?: string | null
  onConfirm: (sendEmail: boolean) => void
  onClose: () => void
}) {
  const [secondStep, setSecondStep] = useState(false)
  const [sendEmail, setSendEmail] = useState(false)

  useEffect(() => {
    if (open) {
      setSecondStep(false)
      setSendEmail(true)
    }
  }, [open])

  const handleConfirm = () => {
    if (submitting) return
    if (!secondStep) {
      setSecondStep(true)
      return
    }
    onConfirm(sendEmail)
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Potvrdit success fee — {fundName}</DialogTitle>
      <DialogContent>
        {secondStep ? (
          <DialogContentText sx={{ color: 'error.main' }}>
            Opravdu? Nevratné: strhne se {totalFeeUnits} jednotek {clientCount} držitelům a sníží se
            celkový počet jednotek fondu.
          </DialogContentText>
        ) : (
          <DialogContentText>
            Strhne se {totalFeeUnits} jednotek {clientCount} držitelům (celkový počet jednotek fondu
            klesne).
          </DialogContentText>
        )}
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Poslat držitelům e-mail s přehledem stržení (kopie distributorovi a na klient@apexcapital.cz)"
          />
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose} disabled={submitting}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          color={secondStep ? 'error' : 'primary'}
          onClick={handleConfirm}
          disabled={submitting}
        >
          {secondStep ? 'Ano, opravdu potvrdit' : 'Potvrdit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface SuccessFeeSectionProps {
  funds: Fund[]
  successFeeRuns: SuccessFeeRun[]
  canManage: boolean
  onChanged: () => void
}

/**
 * Success fee (F6-C): CEO zaklikne splněný cíl → přehled za rok pro držitele (DRAFT)
 * → kontrola → POTVRZENÍ = stržení jednotek + email. UNIQUE per fond+rok. Spouští
 * a potvrzuje backoffice/CEO (F6-D); ostatní read-only.
 */
export function SuccessFeeSection({ funds, successFeeRuns, canManage, onChanged }: SuccessFeeSectionProps) {
  const api = useOpsApi()
  const [fundKey, setFundKey] = useState<StrategyKey>('SEMI')
  const [year, setYear] = useState(String(currentYear()))
  const [priceText, setPriceText] = useState('')
  const [creating, setCreating] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Chyba potvrzení/smazání patří DOVNITŘ dialogu — v těle stránky by ji
  // překryl backdrop modálu a obsluha by klikala znovu.
  const [actionError, setActionError] = useState<string | null>(null)
  const [viewRunId, setViewRunId] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [distributors, setDistributors] = useState<Distributor[]>([])

  useEffect(() => {
    let alive = true
    api
      .listDistributors()
      .then((list) => alive && setDistributors(list))
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [api])

  const fund = useMemo(() => funds.find((f) => f.key === fundKey), [funds, fundKey])
  const fundRuns = useMemo(
    () => successFeeRuns.filter((r) => r.fundKey === fundKey),
    [successFeeRuns, fundKey]
  )
  const viewRun = fundRuns.find((r) => r.id === viewRunId) ?? fundRuns[0]

  // Součty per distributor pro vyúčtování provize (jen řádky s přiřazeným distributorem).
  const distributorTotals = useMemo(() => {
    if (!viewRun) return []
    const names = new Map(distributors.map((d) => [d.id, d.name]))
    const byId = new Map<string, { id: string; name: string; clients: number; fee: number; commission: number }>()
    for (const l of viewRun.lines) {
      if (!l.distributorId) continue
      const row = byId.get(l.distributorId) ?? {
        id: l.distributorId,
        name: names.get(l.distributorId) ?? 'Distributor ' + l.distributorId,
        clients: 0,
        fee: 0,
        commission: 0,
      }
      row.clients += 1
      row.fee += l.fee
      row.commission += l.distribution ?? 0
      byId.set(l.distributorId, row)
    }
    return Array.from(byId.values()).sort((a, b) => b.commission - a.commission)
  }, [viewRun, distributors])

  // Předvyplnění ceny konce roku posledním oceněním fondu (při přepnutí fondu).
  useEffect(() => {
    setPriceText(fund?.lastValuation ? formatUnits(fund.lastValuation.unitPrice) : '')
    setError(null)
  }, [fund])

  const handleCreate = async () => {
    const price = parseAmount(priceText)
    const yearNum = Number(year)
    if (!Number.isInteger(yearNum)) {
      setError('Zadejte rok')
      return
    }
    if (price === null || !(price > 0)) {
      setError('Zadejte cenu jednotky ke konci roku')
      return
    }
    setCreating(true)
    setError(null)
    try {
      const run = await api.createSuccessFee({ fundKey, year: yearNum, yearEndPrice: price })
      setViewRunId(run.id)
      onChanged()
    } catch (err) {
      setError(apiError(err, 'Přehled success fee selhal'))
    } finally {
      setCreating(false)
    }
  }

  const handleConfirm = async (sendEmail: boolean) => {
    if (!viewRun) return
    setSubmitting(true)
    setActionError(null)
    try {
      await api.confirmSuccessFee(viewRun.id, { sendEmail })
      setConfirmOpen(false)
      onChanged()
    } catch (err) {
      setActionError(apiError(err, 'Potvrzení success fee selhalo'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!viewRun) return
    setSubmitting(true)
    setActionError(null)
    try {
      await api.deleteSuccessFee(viewRun.id)
      setDeleteOpen(false)
      setViewRunId(null)
      onChanged()
    } catch (err) {
      setActionError(apiError(err, 'Smazání přehledu selhalo'))
    } finally {
      setSubmitting(false)
    }
  }

  const isDraft = viewRun?.status === 'DRAFT'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SegmentedTabs
        items={funds.map((f) => ({ value: f.key, label: f.key }))}
        value={fundKey}
        onChange={(key) => {
          setFundKey(key as StrategyKey)
          setViewRunId(null)
        }}
      />

      <SectionCard
        title="Success fee — roční přepočet (splněný cíl)"
        info={
          <InfoHint
            title="Výkonnostní odměna dle smlouvy"
            lines={[
              'Počítá se PER VKLAD: zisk vkladu = jednotky × cena konec roku − výchozí hodnota.',
              'Výchozí hodnota = investovaná částka; po prvním zúčtování nejvyšší cena jednotky z minulých zúčtování (high-water mark) — už zpoplatněný zisk se znovu neplatí.',
              'Podmínka: zhodnocení musí PŘEVÝŠIT 7 % p.a. (konvence Actual/360, období od posledního zúčtování, resp. od nákupu). Pod hranicí je fee 0.',
              'Při splnění se platí sazba třídy (A/EB 15 %, B/NORM 20 %) z celého zisku vkladu.',
              'Potvrzení zruší klientovi jednotky v hodnotě fee — celá čísla směrem dolů; klient úbytek vidí.',
              'Jednotky vkladů se poměrově přepočítávají na aktuální držbu (stržení z minulých let).',
              'Distribuce = fee × nasmlouvaný podíl distributora klienta (Distributoři → Podíl SF); bez podílu se použije výchozí sazba fondu.',
            ]}
            sx={{ color: '#fff' }}
          />
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Rok"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              disabled={!canManage || creating}
              sx={{ width: 120 }}
            />
            <TextField
              size="small"
              label="Cena jednotky konec roku"
              value={priceText}
              onChange={(e) => setPriceText(e.target.value)}
              disabled={!canManage || creating}
              sx={{ width: 220 }}
            />
            <Button variant="contained" onClick={handleCreate} disabled={!canManage || creating}>
              Spustit přehled (cíl splněn)
            </Button>
            {!canManage && (
              <Typography variant="caption" color="text.secondary">
                Spouští backoffice nebo CEO.
              </Typography>
            )}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info" variant="outlined">
            Success fee dle smlouvy, per vklad: zisk = jednotky × cena konec roku − výchozí hodnota,
            kde výchozí hodnota je investice, resp. nejvyšší cena jednotky z minulých zúčtování
            (high-water mark — už zpoplatněný zisk se znovu neplatí). Podmínka: zhodnocení musí
            převýšit 7 % p.a. (Actual/360, od posledního zúčtování). Při splnění se platí sazba
            třídy (A/EB 15 %, B/NORM 20 %) z celého zisku. Potvrzení strhne jednotky přímo
            držitelům, zaokrouhlené na celé kusy dolů (klient to vidí).
          </Alert>
        </Box>
      </SectionCard>

      {viewRun && fund && (
        <SectionCard
          title={`Success fee ${fund.key} ${viewRun.year}`}
          action={<StatusChip status={viewRun.status} />}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Cena konce roku {formatUnits(viewRun.yearEndPrice)} · benchmark{' '}
              {((viewRun.benchmarkPa ?? 0.07) * 100).toFixed(0)} % p.a. · celkem fee{' '}
              {formatCzk(viewRun.totalFee)} · stržené jednotky {formatUnits(viewRun.totalFeeUnits)} ·
              distribuce {formatCzk(viewRun.totalDistribution)}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Držitel / datum vkladu</TableCell>
                    <TableCell align="right">Jednotky</TableCell>
                    <TableCell align="right">Investováno</TableCell>
                    <TableCell align="right">Nákupní cena</TableCell>
                    <TableCell align="right">Hodnota</TableCell>
                    <TableCell align="right">Zisk</TableCell>
                    <TableCell align="right">
                      Zhodnocení p.a.{' '}
                      <InfoHint
                        lines={[
                          'Zhodnocení vkladu nad výchozí hodnotou, přepočtené na p.a. konvencí Actual/360.',
                          'Období běží od posledního zúčtování success fee, u prvního od data vkladu.',
                        ]}
                      />
                    </TableCell>
                    <TableCell align="center">
                      Benchmark{' '}
                      <InfoHint
                        lines={[
                          'ANO = zhodnocení PŘEVÝŠILO 7 % p.a. → fee se platí z celého zisku vkladu.',
                          'NE = fee za tento vklad je 0, i když je v zisku.',
                        ]}
                      />
                    </TableCell>
                    <TableCell align="right">Fee</TableCell>
                    <TableCell align="right">
                      Stržené jednotky{' '}
                      <InfoHint
                        lines={[
                          'fee / cena konce roku, zaokrouhleno na CELÁ čísla směrem dolů (dle smlouvy).',
                          'Potvrzením se tyto jednotky klientovi zruší — úbytek vidí ve svém přehledu.',
                        ]}
                      />
                    </TableCell>
                    <TableCell align="right">Vyúčtování klienta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewRun.lines.map((l) => (
                    <React.Fragment key={l.clientId}>
                      <TableRow sx={{ '& td': { fontWeight: 600 } }}>
                        <TableCell>{l.clientName}</TableCell>
                        <TableCell align="right">{formatUnits(l.units)}</TableCell>
                        <TableCell align="right">{formatCzk(l.invested)}</TableCell>
                        <TableCell align="right" />
                        <TableCell align="right">{formatCzk(l.value)}</TableCell>
                        <TableCell align="right">{formatCzk(l.profit)}</TableCell>
                        <TableCell align="right" />
                        <TableCell align="center" />
                        <TableCell align="right">{formatCzk(l.fee)}</TableCell>
                        <TableCell align="right">{formatUnits(l.feeUnits)}</TableCell>
                        <TableCell align="right">
                          <StatementButtons
                            title={'Výpočet success fee ' + viewRun.year + ', všechny strategie, ' + l.clientName}
                            load={(format) => api.successFeeYearStatement(viewRun.year, { clientId: l.clientId }, format)}
                            onError={setError}
                          />
                        </TableCell>
                      </TableRow>
                      {(l.tranches || []).map((t, index) => (
                        <TableRow key={l.clientId + '-' + index}>
                          <TableCell sx={{ pl: 4, color: 'text.secondary' }}>
                            {t.date ? new Date(t.date).toLocaleDateString('cs-CZ') : 'bez data'}
                          </TableCell>
                          <TableCell align="right">{formatUnits(t.units)}</TableCell>
                          <TableCell align="right">{formatCzk(t.invested)}</TableCell>
                          <TableCell align="right">
                            {t.buyPrice !== null && t.buyPrice !== undefined ? formatUnits(t.buyPrice) : '—'}
                          </TableCell>
                          <TableCell align="right">{formatCzk(t.value)}</TableCell>
                          <TableCell align="right">{formatCzk(t.profit)}</TableCell>
                          <TableCell align="right">{(t.returnPa * 100).toFixed(2)} %</TableCell>
                          <TableCell align="center">{t.benchmarkMet ? 'ANO' : 'NE'}</TableCell>
                          <TableCell align="right">{formatCzk(t.fee)}</TableCell>
                          <TableCell align="right" />
                          <TableCell align="right" />
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {distributorTotals.length > 0 && (
              <Box sx={{ overflowX: 'auto' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Vyúčtování pro distributory
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Distributor</TableCell>
                      <TableCell align="right">Klientů</TableCell>
                      <TableCell align="right">Success fee</TableCell>
                      <TableCell align="right">Provize</TableCell>
                      <TableCell align="right">Vyúčtování</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {distributorTotals.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell>{d.name}</TableCell>
                        <TableCell align="right">{d.clients}</TableCell>
                        <TableCell align="right">{formatCzk(d.fee)}</TableCell>
                        <TableCell align="right">{formatCzk(d.commission)}</TableCell>
                        <TableCell align="right">
                          <StatementButtons
                            title={'Provize ze success fee ' + viewRun.year + ', všechny strategie, ' + d.name}
                            load={(format) => api.successFeeYearStatement(viewRun.year, { distributorId: d.id }, format)}
                            onError={setError}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {viewRun.check && (
              <FeeChecksPanel check={viewRun.check} aiTitle="AI kontrola (per klient)" />
            )}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadSuccessFeeReport(viewRun, fund.name)}
                disabled={submitting}
              >
                Stáhnout report
              </Button>
              {isDraft && canManage && (
                <>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setActionError(null)
                      setDeleteOpen(true)
                    }}
                    disabled={submitting}
                  >
                    Smazat
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setActionError(null)
                      setConfirmOpen(true)
                    }}
                    disabled={submitting}
                  >
                    Potvrdit stržení
                  </Button>
                </>
              )}
              {!isDraft && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {viewRun.decidedBy
                    ? `Potvrdil ${viewRun.decidedBy}`
                    : 'Přehled je uzavřen.'}
                </Typography>
              )}
            </Box>
          </Box>
        </SectionCard>
      )}

      {fundRuns.length > 1 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Historie ({fundKey})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {fundRuns.map((r) => (
              <Button
                key={r.id}
                size="small"
                variant={r.id === viewRun?.id ? 'contained' : 'outlined'}
                onClick={() => setViewRunId(r.id)}
              >
                {r.year} · {r.status === 'CONFIRMED' ? 'potvrzeno' : 'návrh'}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      <ConfirmSuccessFeeDialog
        open={confirmOpen}
        fundName={fund?.name ?? fundKey}
        totalFeeUnits={formatUnits(viewRun?.totalFeeUnits ?? 0)}
        clientCount={viewRun?.lines.length ?? 0}
        submitting={submitting}
        error={actionError}
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
      <ConfirmDialog
        open={deleteOpen}
        title="Smazat přehled success fee"
        text="Smaže se nepotvrzený přehled. Jednotky se nestrhnou."
        confirmLabel="smazat"
        danger
        doubleConfirm
        submitting={submitting}
        error={actionError}
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />
    </Box>
  )
}
